import React, { useState } from 'react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Settings, Database, Globe, Info } from 'lucide-react';
import { SupermetricsIngestion } from './SupermetricsIngestion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

interface SettingsPanelProps {
  onDataRefresh?: () => void;
}

export function SettingsPanel({ onDataRefresh }: SettingsPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>

      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Dashboard Settings</SheetTitle>
          <SheetDescription>
            Manage data sources, imports, and dashboard configuration
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="data-import" className="mt-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="data-import" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Data Import
            </TabsTrigger>
            <TabsTrigger value="api-info" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              API Info
            </TabsTrigger>
            <TabsTrigger value="about" className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              About
            </TabsTrigger>
          </TabsList>

          <TabsContent value="data-import" className="space-y-6 mt-6">
            <SupermetricsIngestion 
              onDataIngested={() => {
                onDataRefresh?.();
                setIsOpen(false);
              }} 
            />
          </TabsContent>

          <TabsContent value="api-info" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Current Data Source</CardTitle>
                <CardDescription>
                  Information about the active data source and configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Primary Source</span>
                  <Badge variant="secondary">Supabase Database</Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Table</span>
                  <Badge variant="outline">adcanvas_facebook_ads</Badge>
                </div>
                
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Data Integration</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Direct API calls from client</li>
                    <li>• Data stored in Supabase automatically</li>
                    <li>• Support for multiple date ranges</li>
                    <li>• Week-over-week comparison data</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="about" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>AdCanvas</CardTitle>
                <CardDescription>
                  A comprehensive Facebook Ads creative performance dashboard
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium">Version</div>
                    <div className="text-muted-foreground">2.0.0</div>
                  </div>
                  <div>
                    <div className="font-medium">Last Updated</div>
                    <div className="text-muted-foreground">Dec 2024</div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Features</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Responsive card grid layout</li>
                    <li>• KPI metrics with WoW deltas</li>
                    <li>• Advanced filtering and sorting</li>
                    <li>• CSV export functionality</li>
                    <li>• Creative preview modals</li>
                    <li>• Real-time data integration</li>
                  </ul>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Data Sources</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Facebook Ads data connector</li>
                    <li>• Supabase database storage</li>
                    <li>• Real-time metric comparisons</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}